import { getCloudflareHeaders } from '~/utils/getCloudflareHeaders';
import getCookieValue from '~/utils/getCookieValue';
import type { NectarPageBlock } from '~/utils/nectarPageBlock';
import {
  NECTAR_ANONYMOUS_USER_ID_FIELD,
  NECTAR_IS_PWA_FIELD,
  NECTAR_PAGE_BLOCK_FIELD,
  NECTAR_SITE_ID_FIELD,
} from '~/utils/nectarTrackingFields';
import { generateUUID } from '~/utils/uuid';

export const normalizeOptionalFormValue = (value: FormDataEntryValue | null): string | undefined => {
  if (value == null) return undefined;
  const stringValue = value.toString();
  return stringValue && stringValue !== 'undefined' && stringValue !== 'null' ? stringValue : undefined;
};

// In-memory storage for anonymous user ID when localStorage is unavailable
let inMemoryAnonymousId: string | null = null;

export const getOrCreateAnonymousUserId = (): string => {
  const STORAGE_KEY = 'beehiiv_anonymous_user_id';

  try {
    let anonymousId = localStorage.getItem(STORAGE_KEY);

    if (!anonymousId) {
      anonymousId = generateUUID();
      localStorage.setItem(STORAGE_KEY, anonymousId);
    }

    return anonymousId;
  } catch {
    if (!inMemoryAnonymousId) {
      inMemoryAnonymousId = generateUUID();
    }
    return inMemoryAnonymousId;
  }
};

export const isPwaDisplayMode = (): boolean => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;

  return window.matchMedia('(display-mode: standalone)').matches;
};

export const getNectarVisitToken = (): string => {
  if (typeof document === 'undefined') return '';
  return getCookieValue('visit_token', document.cookie) || '';
};

export function withNectarTrackingParams<T extends Record<string, string>>(
  payload: T,
  pageBlock?: NectarPageBlock,
  siteId?: string,
): T & {
  [NECTAR_ANONYMOUS_USER_ID_FIELD]: string;
  [NECTAR_IS_PWA_FIELD]: string;
  [NECTAR_PAGE_BLOCK_FIELD]?: string;
  [NECTAR_SITE_ID_FIELD]?: string;
};

export function withNectarTrackingParams(formData: FormData, pageBlock?: NectarPageBlock, siteId?: string): void;

export function withNectarTrackingParams(
  payload: Record<string, string> | FormData,
  pageBlock?: NectarPageBlock,
  siteId?: string,
) {
  const anonymousUserId = getOrCreateAnonymousUserId();
  const isPwa = isPwaDisplayMode() ? 'true' : 'false';

  if (payload instanceof FormData) {
    payload.set(NECTAR_ANONYMOUS_USER_ID_FIELD, anonymousUserId);
    payload.set(NECTAR_IS_PWA_FIELD, isPwa);
    if (pageBlock) {
      payload.set(NECTAR_PAGE_BLOCK_FIELD, pageBlock);
    }
    if (siteId) {
      payload.set(NECTAR_SITE_ID_FIELD, siteId);
    }
    return;
  }

  return {
    ...payload,
    [NECTAR_ANONYMOUS_USER_ID_FIELD]: anonymousUserId,
    [NECTAR_IS_PWA_FIELD]: isPwa,
    ...(pageBlock ? { [NECTAR_PAGE_BLOCK_FIELD]: pageBlock } : {}),
    ...(siteId ? { [NECTAR_SITE_ID_FIELD]: siteId } : {}),
  };
}

export const nectarAnonymousUserIdFromForm = (body: FormData): string | undefined =>
  normalizeOptionalFormValue(body.get(NECTAR_ANONYMOUS_USER_ID_FIELD));

export const nectarIsPwaFromForm = (body: FormData): boolean | undefined => {
  const value = normalizeOptionalFormValue(body.get(NECTAR_IS_PWA_FIELD));
  if (value == null) return undefined;

  return value === 'true';
};

export const nectarPageBlockFromForm = (body: FormData): string | undefined =>
  normalizeOptionalFormValue(body.get(NECTAR_PAGE_BLOCK_FIELD));

export const nectarSiteIdFromForm = (body: FormData): string | undefined =>
  normalizeOptionalFormValue(body.get(NECTAR_SITE_ID_FIELD));

export const getNectarParams = (
  nectarAnonymousUserId: string | undefined,
  nectarIsPwa: boolean | undefined,
  nectarPageBlock: string | undefined,
  nectarSiteId?: string | undefined,
): Record<string, unknown> => ({
  ...(nectarAnonymousUserId ? { [NECTAR_ANONYMOUS_USER_ID_FIELD]: nectarAnonymousUserId } : {}),
  ...(nectarIsPwa != null ? { [NECTAR_IS_PWA_FIELD]: nectarIsPwa } : {}),
  ...(nectarPageBlock ? { [NECTAR_PAGE_BLOCK_FIELD]: nectarPageBlock } : {}),
  ...(nectarSiteId ? { [NECTAR_SITE_ID_FIELD]: nectarSiteId } : {}),
});

export const getNectarCommunityMutationBody = (form: FormData, request: Request): Record<string, unknown> => ({
  ...getCloudflareHeaders(request),
  ...getNectarParams(nectarAnonymousUserIdFromForm(form), nectarIsPwaFromForm(form), nectarPageBlockFromForm(form)),
});
